<template>
  <ArticleDetailSkeleton v-if="props.loading" />

  <template v-else>
    <!-- Contenu détail article -->
    <!-- ── HERO BANNER avec IMAGE + LOGO ─────────────────────────────────────────────── -->
    <PrepeersHeroCover
      :src="articleData?.articledatas.coverPict"
      alt="Article PrePeers" />

    <PrepeersBreadcrumb
      auto
      section="nos-conseils"
      :current-label="articleData?.articledatas?.h1" />

    <!-- ── HERO + H1 ─────────────────────────────────────────────── -->
    <section class="hero-section">
      <div class="container">
        <h1 class="hero-section__h1">{{ articleData?.articledatas?.h1 }}</h1>
        <div
          class="hero-section__intro"
          v-html="formatSeoIntro(articleData?.articledatas?.introduction ?? '')" />
        <BlogArticleMeta
          v-if="articleData?.articledatas?.timeReading"
          :reading-time="articleData?.articledatas?.timeReading"
          :published-at="articleData?.articledatas?.datePublished"
          class="hero-section__publisheddate" />
        <div class="hero-section__line" />
      </div>
    </section>

    <!-- ── LIENS PAGE PARENT ─────────────────────────────────────────────── -->
    <section
      v-if="articleData?.parsedParentBlock"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ articleData?.parsedParentBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in articleData?.parsedParentBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── CTA SI NON CONNECTÉ ─────────────────────────────────────────────── -->
    <section
      v-if="!isLoggedIn"
      class="cta-section">
      <div class="container">
        <PrePeersLolaCta
          variant="blog"
          :entity-name="articleData?.articledatas?.h1" />
      </div>
    </section>

    <!-- ── CONTENU HTML RICHE ─────────────────────────────────────────────── -->
    <section
      v-if="articleData?.articledatas.contentText"
      class="content-section">
      <div class="container">
        <UiPrepeersQlEditorContent :html="articleData?.articledatas.contentText ?? ''" />
      </div>
    </section>

    <section
      v-if="articleData?.articledatas?.authorFirstName"
      class="author-section">
      <BlogAuthorBadge
        :author-name="articleData?.articledatas?.authorFirstName"
        :avatar-url="articleData?.articledatas?.authorPictSrc" />
    </section>

    <section
      v-if="articleData?.articledatas?.authorFirstName"
      class="author-section">
      <UiSocialShare
        title="Partagez cet article sur les réseaux sociaux !"
        :url="currentUrl"
        :article-title="articleData?.articledatas?.h1" />
    </section>

    <!-- ── LIENS PARTNERS ─────────────────────────────────────────────── -->
    <section
      v-if="articleData?.parsedPartners.length"
      class="media-grid-section">
      <div class="container">
        <h2 class="media-grid__title">{{ t('seointer.root.title_partners') }}</h2>
        <p class="media-grid__desc">{{ t('seointer.root.description_partners') }}</p>
        <div class="media-grid__cards">
          <UiSchoolCard
            v-for="school in articleData?.parsedPartners"
            :key="school.id"
            :school="school" />
        </div>
      </div>
    </section>

    <!-- ── LIENS ARTICLES BLOG ─────────────────────────────────────────────── -->
    <section
      v-if="articleData?.parsedArticles.length"
      class="media-grid-section">
      <div class="container">
        <h2 class="media-grid__title">{{ t('seointer.root.title_articles') }}</h2>
        <p class="media-grid__desc">{{ t('seointer.root.description_articles') }}</p>
        <div class="media-grid__cards">
          <UiArticleCard
            v-for="article in articleData?.parsedArticles"
            :key="article.id"
            :article="article" />
        </div>
      </div>
    </section>
  </template>
</template>

<script setup lang="ts">
import ArticleDetailSkeleton from '~/components/blog/ArticleDetailSkeleton.vue'
import PrepeersBreadcrumb from '~/components/navigation/PrepeersBreadcrumb.vue'
import type { ArticleDataResponse } from '~/types/article'
import PrePeersLolaCta from '~/components/ui/PrePeersLolaCta.vue'
import PrepeersHeroCover from '~/components/core/PrepeersHeroCover.vue'
import { formatSeoIntro } from '~/utils/helpers'

interface BlogArticleProps {
  articleData: ArticleDataResponse | null
  loading?: boolean
  isLoggedIn: boolean
}

const props = withDefaults(defineProps<BlogArticleProps>(), {
  loading: false,
})

const localePath = useLocalePath()
const { t } = useI18n()

const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

watch(
  () => props.articleData,
  (_newVal) => {
    //console.log('[Article] props.articleData CHANGED:', _newVal?.articledatas?.coverPict)
  },
  { immediate: true, deep: true },
)
</script>

<style scoped>
.author-section {
  text-align: center;
  margin: var(--space-8);
}
</style>
